/**
 * Folha "Mais" (bottom sheet), aberta pela aba "Mais" da barra inferior: campo "Pesquisar página"
 * no topo, três grupos (sketch 010, tela 9a) e, no rodapé, "Editar layout" e "Sair".
 * Com texto na busca, os grupos dão lugar aos resultados (mesmas páginas e sinônimos da paleta
 * Ctrl K, sem pacientes); Enter abre o primeiro e Esc limpa a busca.
 * Montado uma vez em AppShell. Padrão adaptado de
 * steel-app-au/src/platform/app-shell/navigation/MobileNavMenuSheet.tsx às convenções Synap:
 * createPortal + scrim + pilha única de overlays (`useOverlay`: voltar web/nativo e Esc fecham,
 * foco preso) + fecha antes de navegar (`closeThen`, BACK-07).
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import type { UserRole } from '@/entities/session/types'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { useHomeLayoutStore } from '@/features/homeLayout/homeLayoutStore'
import { cn } from '@/shared/lib/cn'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { useGuardedNavigate } from '@/shared/lib/useGuardedNavigate'
import { Button } from '@/shared/ui/Button'
import { ChevronRightIcon, CloseIcon, LogOutIcon, SearchIcon } from '@/shared/ui/icons'
import { useConfirmDialog } from '@/shared/ui/useConfirmDialog'
import {
  EDIT_HOME_LAYOUT_ITEM,
  getMobileMenuGroups,
  type MobileMenuItem,
} from '@/widgets/navigation/mobileMenuItems'
import { MobileMenuSubtitle } from '@/widgets/navigation/MobileMenuSubtitle'
import { getAccountMenuMobileLinks } from '@/widgets/navigation/accountMenuItems'
import { filterGoToPageItems } from '@/widgets/navigation/appNavigationCatalog'
import { getGoToPageItems, type GoToPageItem } from '@/widgets/navigation/goToPageItems'
import { rememberRecentPath } from '@/widgets/navigation/goToRecent'
import { useMobileMenuStore } from '@/widgets/navigation/mobileMenuStore'

const GROUP_LABELS = {
  frequent: 'Mais usadas',
  tools: 'Ferramentas',
  account: 'Conta',
} as const

const isHomeRoute = (pathname: string) => pathname === '/' || pathname === '/dashboard'

/** Páginas da busca da folha: as da paleta mais os atalhos da conta (Documentação, Novidades…). */
const getSheetSearchItems = (role: UserRole, isSystemAdmin: boolean): GoToPageItem[] => {
  const pages = getGoToPageItems({ role, isSystemAdmin })
  const seen = new Set(pages.map((item) => item.to))
  const account = getAccountMenuMobileLinks({ role, isSystemAdmin })
    .filter((item) => !seen.has(item.to))
    .map((item) => ({ to: item.to, label: item.label, group: GROUP_LABELS.account }))
  return [...pages, ...account]
}

/** Duração da saída (ms) — deve casar com a classe `duration-[140ms]` do painel. */
const EXIT_DURATION_MS = 140

export const MobileMenuSheet = () => {
  const open = useMobileMenuStore((s) => s.open)
  const setOpen = useMobileMenuStore((s) => s.setOpen)
  const role = useSessionStore((s) => (s.user?.role ?? 'doctor') as UserRole)
  const isSystemAdmin = useSessionStore((s) => s.user?.isSystemAdmin ?? false)
  const userId = useSessionStore((s) => s.user?.id ?? null)
  const logout = useSessionStore((s) => s.logout)
  const setHomeLayoutEditing = useHomeLayoutStore((s) => s.setEditing)
  const { confirm } = useConfirmDialog()
  const guardedNavigate = useGuardedNavigate()
  const location = useLocation()

  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  // Mantém a folha no DOM durante a animação de saída; desmonta ao final.
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sincroniza montagem/animação com `open` externo (store)
      setMounted(true)
      const raf = window.requestAnimationFrame(() => setVisible(true))
      return () => window.cancelAnimationFrame(raf)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), EXIT_DURATION_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  // Voltar (web e nativo), Esc e foco (vai ao ✕, preso na folha, volta ao gatilho): pilha única.
  const { closeThen } = useOverlay(open, {
    kind: 'sheet',
    label: 'Mais',
    onRequestClose: () => setOpen(false),
    panelRef,
    initialFocusRef: closeButtonRef,
    initialFocusDelayMs: 20,
  })

  const close = useCallback(() => setOpen(false), [setOpen])

  const [query, setQuery] = useState('')
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) setQuery('')
  }
  const searching = query.trim().length > 0
  const searchItems = useMemo(() => getSheetSearchItems(role, isSystemAdmin), [role, isSystemAdmin])
  const searchResults = useMemo(
    () => (searching ? filterGoToPageItems(searchItems, query) : []),
    [searchItems, query, searching],
  )

  const openSearchResult = useCallback(
    (item: GoToPageItem) => {
      rememberRecentPath(userId, item.to)
      closeThen(() => {
        void guardedNavigate(item.to, item.state ? { state: item.state } : undefined)
      })
    },
    [closeThen, guardedNavigate, userId],
  )

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchResults[0]) {
      event.preventDefault()
      openSearchResult(searchResults[0])
    } else if (event.key === 'Escape' && query) {
      // Esc com texto limpa a busca; o próximo Esc fecha a folha.
      event.preventDefault()
      setQuery('')
    }
  }

  const handleItemClick = useCallback(
    (item: MobileMenuItem) => {
      if (item.kind === 'action') {
        if (item.action === 'edit-home-layout') {
          closeThen(() => {
            if (!isHomeRoute(location.pathname)) {
              void guardedNavigate('/dashboard')
            }
            setHomeLayoutEditing(true)
          })
        }
        return
      }
      closeThen(() => {
        void guardedNavigate(item.to, item.state ? { state: item.state } : undefined)
      })
    },
    [closeThen, location.pathname, guardedNavigate, setHomeLayoutEditing],
  )

  const handleLogoutClick = useCallback(() => {
    closeThen(() => {
      void (async () => {
        const ok = await confirm({
          title: 'Sair da conta?',
          description: 'Tem certeza de que deseja sair?',
          confirmLabel: 'Sair',
          cancelLabel: 'Cancelar',
          tone: 'danger',
        })
        if (ok) await logout()
      })()
    })
  }, [closeThen, confirm, logout])

  if (!mounted) return null

  const groups = getMobileMenuGroups(role, isSystemAdmin)
  const itemClassName =
    'flex min-h-[44px] w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-page)] active:bg-[var(--color-mint-soft)]'

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[120] flex flex-col justify-end bg-black/50 md:hidden',
        'transition-opacity duration-200',
        visible ? 'opacity-100' : 'opacity-0',
      )}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close()
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mais"
        className={cn(
          'max-h-[80vh] w-full overflow-y-auto rounded-t-2xl border-t border-[var(--color-border)]',
          'bg-[var(--color-bg-surface)] p-3 shadow-[0_-8px_30px_rgba(0,0,0,0.25)]',
          'transition-[transform,opacity] motion-reduce:transform-none motion-reduce:transition-opacity',
          visible
            ? 'translate-y-0 opacity-100 duration-[220ms] ease-out'
            : 'translate-y-full opacity-0 duration-[140ms] ease-in',
        )}
      >
        <div className="mb-2 flex items-center justify-between px-1">
          <span className="text-sm font-bold text-[var(--color-text-primary)]">Mais</span>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={close}
            aria-label="Fechar menu"
            className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-page)] active:bg-[var(--color-mint-soft)]"
          >
            <CloseIcon size={20} aria-hidden />
          </button>
        </div>
        <div className="relative mb-2 px-1" role="search">
          <SearchIcon
            size={16}
            aria-hidden
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Pesquisar página"
            aria-label="Pesquisar página"
            aria-controls={searching ? 'synap-sheet-search-results' : undefined}
            autoComplete="off"
            enterKeyHint="go"
            className="h-10 w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-page)] pl-9 pr-10 text-base text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-mint-strong)] [&::-webkit-search-cancel-button]:hidden"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Limpar busca"
              className="absolute right-1 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)]"
            >
              <CloseIcon size={16} aria-hidden />
            </button>
          ) : null}
        </div>
        {searching ? (
          searchResults.length === 0 ? (
            <p
              role="status"
              className="px-3 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-center text-sm text-[var(--color-text-secondary)]"
            >
              Nenhuma página encontrada.
            </p>
          ) : (
            <ul
              id="synap-sheet-search-results"
              aria-label="Páginas encontradas"
              className="space-y-0.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
            >
              {searchResults.map((item) => (
                <li key={`${item.to}-${item.label}`}>
                  <button type="button" onClick={() => openSearchResult(item)} className={itemClassName}>
                    <ChevronRightIcon size={18} className="shrink-0 text-[var(--color-text-secondary)]" />
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    <span className="shrink-0 text-xs font-medium text-[var(--color-text-muted)]">
                      {item.group}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <>
          {groups.map((group) => (
            <ul
              key={group.id}
              aria-label={GROUP_LABELS[group.id]}
              className="space-y-0.5 border-t border-[var(--color-border)] py-1 first-of-type:border-t-0"
            >
              {group.items.map((item) => (
                <li key={item.label}>
                  <button type="button" onClick={() => handleItemClick(item)} className={itemClassName}>
                    <item.icon size={18} className="shrink-0 text-[var(--color-text-secondary)]" />
                    <span className="min-w-0 flex-1">
                      <span className="block">{item.label}</span>
                      {item.subtitle ? <MobileMenuSubtitle kind={item.subtitle} /> : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ))}
          <div className="mt-1 space-y-1 border-t border-[var(--color-border)] pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={() => handleItemClick(EDIT_HOME_LAYOUT_ITEM)}
              className={itemClassName}
            >
              <EDIT_HOME_LAYOUT_ITEM.icon size={18} className="shrink-0 text-[var(--color-text-secondary)]" />
              <span className="min-w-0 flex-1">{EDIT_HOME_LAYOUT_ITEM.label}</span>
            </button>
            <Button
              type="button"
              variant="destructive"
              fullWidth
              onClick={handleLogoutClick}
              aria-label="Sair da conta"
              className="flex items-center justify-start gap-3"
            >
              <LogOutIcon size={18} className="shrink-0" />
              Sair
            </Button>
          </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
